import { useRef } from "react";

import { Flex } from "@similarweb/ui-components/foundations";

import { useInViewport } from "app/custom-hooks/useInViewport";
import { translate } from "app/filters/i18nFilter";
import { COMPANY_ANALYSIS_COMPETITORS } from "app/pages/sales-intelligence/constants/routes";
import { useCompanyName } from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCompanyName";
import useCompetitorsQuery from "app/pages/sales-intelligence/modules/company-analysis/queries/useCompetitorsQuery";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import CompetitorsCardContent from "app/pages/sales-intelligence/pages/company-analysis/components/cards/competitors-card/CompetitorsCardContent/CompetitorsCardContent";
import Card from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Card/Card";
import CardFooter from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Card/CardFooter/CardFooter";
import CardHeader from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Card/CardHeader/CardHeader";
import CardRowsSkeleton from "app/pages/sales-intelligence/pages/company-analysis/components/shared/CardRowsSkeleton/CardRowsSkeleton";
import {
    COMPANY_ANALYSIS_TAB_NAMES,
    LAZY_CARD_VIEWPORT_OPTIONS,
} from "app/pages/sales-intelligence/pages/company-analysis/constants";

const CompetitorsCard = () => {
    const cardRef = useRef<HTMLDivElement>(null);
    const isVisible = useInViewport(cardRef, LAZY_CARD_VIEWPORT_OPTIONS);

    const { data, isLoading, isFetched } = useCompetitorsQuery({
        limit: 5,
        includeSignalsCount: true,
        enabled: isVisible,
    });
    const isEmpty = (!data || data.length === 0) && isFetched;
    const isCardLoading = !isVisible || isLoading;

    const companyName = useCompanyName();

    return (
        <Card
            ref={cardRef}
            dataAutomation="competitors-card"
            isLoading={isCardLoading}
            loader={<CardRowsSkeleton rowHeight={62} />}
            isEmpty={isEmpty}
            header={
                <CardHeader
                    title={translate("si.company-analysis.competitors.card.header")}
                    infoTooltip={translate("si.company-analysis.competitors.card.header.info-tooltip")}
                />
            }
            footer={
                <CardFooter
                    stateId={COMPANY_ANALYSIS_COMPETITORS}
                    isLoading={isCardLoading}
                    onClick={() => {
                        companyAnalysisTrackingService.widgets.trackWidgetFooterButtonClick(
                            companyName,
                            COMPANY_ANALYSIS_TAB_NAMES.competitors,
                        );
                    }}
                />
            }
            renderContent={() => (
                <Flex
                    justifyContent="flex-start"
                    flexGrow="1"
                    flexDirection="column"
                    flexWrap="wrap"
                    padding="8px 24px"
                    gap="8px"
                    minHeight="316px"
                >
                    <CompetitorsCardContent competitors={data} />
                </Flex>
            )}
            width="50%"
        />
    );
};

export default CompetitorsCard;
